import { Button, cn } from '@repo/ui'
import { BookOpenText, ChevronRight, FileText, Mail, Presentation } from 'lucide-react'
import { useEffect } from 'react'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import type { ConversationMessage, ConversationStaleness } from '@/api/conversation'
import { ComputedResultCard } from './ComputedResult'
import { changeSizeLabel, slideVersionTurn } from './eventCopy'
import { activityDetail, activityGlyph, activityQuiet, activityRow } from './activityLine'
import { useSourceDetails } from './SourceDetails'
import type { SourceDetailsController, SourceSummaryLoader } from './SourceDetails'
import type { ConversationChange, ConversationOriginStatus } from './state'
import type { ChatInspectorIdentity, WorkspaceCanvasIdentity } from './ConversationShell'

function citationDocumentIDs(refs: unknown): Array<number> {
  if (typeof refs !== 'object' || refs === null || Array.isArray(refs)) return []
  const values = (refs as Record<string, unknown>)['docIds']
  if (!Array.isArray(values) || values.length > 256) return []
  if (!values.every((value) => typeof value === 'number' && Number.isSafeInteger(value) && value > 0)) return []
  return [...new Set(values)]
}

// What a turn produced, decided once. The card, its label and where it opens all follow
// from this rather than from which of seven nullable foreign keys happen to be set: a
// chain of existence tests answers "which columns are present", and what the transcript
// needs to know is "what did this turn make". M9.15.
type TurnArtifact = {
  label: string
  action: string
  icon: typeof FileText
  canvas: WorkspaceCanvasIdentity
  // How much chrome the turn spends saying it. A deck, a document or a proposal section is
  // one thing the turn produced, and a card for it is proportionate. A slide is not: a deck
  // build writes one turn per slide, so a card each put three panels of chrome in the
  // transcript for one piece of work. Those get a line instead.
  weight: 'card' | 'inline'
}

export function turnArtifact(message: ConversationMessage): TurnArtifact | undefined {
  const id = (value: number | null | undefined): number | undefined =>
    value === null || value === undefined ? undefined : value
  const proposalId = id(message.proposalId)
  const sectionId = id(message.proposalSectionId)
  const presentationId = id(message.presentationId)
  const slideId = id(message.presentationSlideId)
  const documentId = id(message.documentId)

  if (proposalId !== undefined && sectionId !== undefined) {
    return {
      label: 'Proposal section',
      action: 'Open proposal section',
      icon: FileText,
      weight: 'card',
      canvas: { canvas: 'proposal', proposalId, sectionId }
    }
  }
  if (documentId !== undefined) {
    return {
      label: 'Document',
      action: 'Open document',
      icon: Mail,
      weight: 'card',
      canvas: { canvas: 'document', documentId }
    }
  }
  if (presentationId !== undefined) {
    if (slideId === undefined) {
      return {
        label: 'Presentation',
        action: 'Open presentation',
        icon: Presentation,
        weight: 'card',
        canvas: { canvas: 'presentation', presentationId }
      }
    }
    return {
      // Not `Open slide specification`. The canvas has never rendered a version, so a label
      // promising a specific one was a claim the surface could not keep, and `specification`
      // is how this codebase talks rather than how a reader thinks.
      //
      // The version is gone from the address for the same reason. It reached a cache key and
      // a staleness guard, never a rendered pixel, and the guard turned every link stale the
      // moment the slide was refined again: a turn from three refines back dead-ended on
      // `This slide version is no longer selected.` Naming only the slide is what the label
      // already promised, and it keeps working for as long as the slide exists. The pair rule
      // that voided a half-identified slide goes with it: there is no pair left to guard.
      label: 'Slide',
      action: 'Show this slide',
      icon: Presentation,
      weight: 'inline',
      canvas: { canvas: 'presentation', presentationId, slideId }
    }
  }
  return undefined
}

function ArtifactCard({
  message,
  onOpenCanvas
}: {
  message: ConversationMessage
  onOpenCanvas: (identity: WorkspaceCanvasIdentity) => void
}) {
  const artifact = turnArtifact(message)
  if (artifact === undefined) return null
  const Icon = artifact.icon
  if (artifact.weight === 'inline') {
    return (
      <Button
        type="button"
        size="sm"
        variant="link"
        className="mt-1 h-auto px-0"
        onClick={() => onOpenCanvas(artifact.canvas)}
      >
        <Icon className="size-4" aria-hidden="true" />
        {artifact.action}
      </Button>
    )
  }
  return (
    <div
      data-testid="conversation-artifact-card"
      className="group/artifact @container mt-3 rounded-xl border border-primary/15 bg-card p-3 text-card-foreground shadow-xs transition-[border-color,box-shadow] hover:border-primary/30 hover:shadow-sm"
    >
      <div className="flex flex-col gap-3 @sm:flex-row @sm:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary ring-1 ring-primary/10 transition-colors group-hover/artifact:bg-accent-strong">
            <Icon className="size-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1 text-sm font-semibold">{artifact.label}</span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-full shrink-0 gap-1.5 border-primary/15 bg-background shadow-2xs hover:border-primary/30 hover:bg-accent @sm:ml-auto @sm:w-auto"
          onClick={() => onOpenCanvas(artifact.canvas)}
        >
          {artifact.action}
          <ChevronRight className="size-3.5 transition-transform group-hover/artifact:translate-x-0.5 motion-reduce:transform-none" />
        </Button>
      </div>
    </div>
  )
}

function SourceSummaryAction({
  count,
  messageID,
  sourceDetails,
  onOpenInspector,
  loadNames
}: {
  count: number
  messageID: number
  sourceDetails: SourceDetailsController
  onOpenInspector: (identity: ChatInspectorIdentity) => void
  loadNames: boolean
}) {
  const load = sourceDetails.load
  useEffect(() => {
    if (loadNames) load()
  }, [load, loadNames])
  const firstName = sourceDetails.summaries?.[0]?.fileName.trim()
  const actionLabel = `View ${count} ${count === 1 ? 'source' : 'sources'}`
  const preview = firstName ? `${firstName}${count > 1 ? ` +${count - 1}` : ''}` : undefined
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={actionLabel}
      className="group/sources -ml-2 h-8 max-w-full min-w-0 justify-start gap-1.5 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
      onClick={() => onOpenInspector({ inspector: 'sources', messageId: messageID })}
    >
      <BookOpenText
        className="size-3.5 shrink-0 text-muted-foreground/80 group-hover/sources:text-primary"
        aria-hidden="true"
      />
      <span className="shrink-0">{actionLabel}</span>
      {preview !== undefined && (
        <>
          <span className="text-muted-foreground/40" aria-hidden="true">
            &middot;
          </span>
          <span className="min-w-0 truncate font-normal text-muted-foreground/70">{preview}</span>
        </>
      )}
      <ChevronRight
        className="size-3.5 shrink-0 text-muted-foreground/70 transition-transform group-hover/sources:translate-x-0.5 group-focus-visible/sources:translate-x-0.5 motion-reduce:transform-none"
        aria-hidden="true"
      />
    </Button>
  )
}

/**
 * A slide-version turn: the slide's new title on the line, its key message beneath, and the
 * control that opens it. Shares the activity-line shape with the change projections, so a
 * refine reads the same as the events around it rather than as a paragraph of prose.
 */
function SlideVersionLine({
  title,
  keyMessage,
  happened,
  size,
  onOpen
}: {
  title: string
  keyMessage: string
  happened?: string
  size?: string
  onOpen: () => void
}) {
  return (
    <div className="@container flex flex-col gap-0.5">
      <span className={activityRow}>
        <span className={cn(activityGlyph, activityQuiet)}>
          <Presentation aria-hidden="true" />
        </span>
        {/*
          Name first, verb second. The verb is the same on every one of these lines, so it is
          the half with nothing to distinguish and the half that goes when the row runs out
          of room: hidden below a narrow container rather than ellipsed to `Rewrot...`, which
          spent the name's characters to say nothing. A container query rather than a
          viewport one, because this row is as often in a panel beside a canvas as it is in a
          full-width chat, and the viewport says nothing about which.
        */}
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 truncate">{title}</span>
          {happened !== undefined && (
            <span className="hidden min-w-0 shrink-[3] items-center gap-1.5 @md:flex">
              <span className="shrink-0 text-muted-foreground/50" aria-hidden="true">
                &middot;
              </span>
              <span className="min-w-0 truncate text-muted-foreground/70">{happened}</span>
            </span>
          )}
        </span>
        {size !== undefined && (
          <span
            className="shrink-0 text-xs text-muted-foreground/60 tabular-nums"
            data-testid="conversation-change-size"
          >
            {size}
          </span>
        )}
        <Button type="button" size="sm" variant="link" className="h-auto shrink-0 px-0 text-xs" onClick={onOpen}>
          Show this slide
        </Button>
      </span>
      <span className={cn(activityDetail, 'text-muted-foreground')}>{keyMessage}</span>
    </div>
  )
}

export function ConversationTurn({
  message,
  isCurrentUser,
  showArtifact = true,
  originStatus,
  staleness,
  slideChange,
  onOpenCanvas,
  onOpenInspector,
  loadSourceSummaries
}: {
  message: ConversationMessage
  isCurrentUser: boolean
  showArtifact?: boolean
  originStatus?: ConversationOriginStatus
  staleness?: ConversationStaleness
  // The artifact-change event for the slide version this turn wrote, where one has arrived.
  // It carries the verb and the word delta, neither of which is on the message itself: see
  // selectSlideVersionFacts for the join, and why it lands on the turn rather than beside it.
  slideChange?: ConversationChange
  onOpenCanvas: (identity: WorkspaceCanvasIdentity) => void
  onOpenInspector: (identity: ChatInspectorIdentity) => void
  loadSourceSummaries?: SourceSummaryLoader
}) {
  const citationIDs = citationDocumentIDs(message.refs)
  const sourceDetails = useSourceDetails(citationIDs, loadSourceSummaries)

  if (message.author === 'computed-facts') {
    const results = message.computedResults ?? []
    if (results.length === 0) return null
    return (
      <article data-testid="conversation-turn" className="space-y-2" aria-label="Computed facts">
        {results.length > 1 && <p className="text-xs text-muted-foreground">{results.length} results</p>}
        {results.map((result, index) => (
          <ComputedResultCard
            key={`${message.messageId}:${index}`}
            result={result}
            defaultOpen={results.length === 1}
            onOpenSources={() => onOpenInspector({ inspector: 'sources', messageId: message.messageId })}
          />
        ))}
      </article>
    )
  }

  const isUser = message.author === 'user'
  // The pane holds current state, the chat holds change events. A turn that wrote a
  // document shows the card and the canvas shows the body, so a long draft no longer
  // scrolls the conversation away. Proposal and presentation turns are unchanged: they
  // still show both, and moving them is its own decision. See M9.15 decision 4.
  const artifact = turnArtifact(message)
  const bodyLivesInCanvas = !isUser && artifact?.canvas.canvas === 'document'
  // A refine writes one turn per instruction, each opening with the producer's own
  // `Created slide version:` boilerplate, so four refines printed four near-identical
  // three-line blocks. The news is the slide's new title and message, so a recognised turn
  // renders as the same quiet line a change event does. Unrecognised prose falls through
  // to the markdown body below, which is the only safe way for a producer's wording to
  // change: the line treatment is lost, never the content.
  const slideVersion = !isUser && artifact?.weight === 'inline' ? slideVersionTurn(message.content) : undefined
  const slideSize =
    slideChange?.kind === 'artifact' ? changeSizeLabel(slideChange.words, slideChange.previousWords) : undefined
  return (
    <article
      data-testid="conversation-turn"
      className={cn('group flex w-full', isCurrentUser ? 'justify-end' : 'justify-start')}
      aria-label={isUser ? (isCurrentUser ? 'Your message' : 'Collaborator message') : 'Snowvault response'}
    >
      <div
        className={cn(
          'max-w-[44rem]',
          // An assistant turn is a prose block, not a bubble: without a width it shrinks
          // to its own text, so its artifact card was as wide as the paragraph above it
          // and consecutive Open buttons lined up with nothing. A user turn keeps hugging
          // its text, which is the one case where shrinking is what a reader expects.
          !isUser && 'w-full',
          isUser && 'max-w-[34rem] rounded-2xl bg-muted px-4 py-2',
          isCurrentUser && 'rounded-br-md',
          isUser && !isCurrentUser && 'rounded-bl-md'
        )}
      >
        {isUser ? (
          <>
            <p className="text-sm leading-6 whitespace-pre-wrap">{message.content}</p>
            {originStatus === 'left-unfinished' && (
              <p className="mt-1 text-xs leading-4 text-muted-foreground">Left unfinished</p>
            )}
          </>
        ) : bodyLivesInCanvas ? null : slideVersion !== undefined ? (
          <SlideVersionLine
            title={slideVersion.title}
            keyMessage={slideVersion.keyMessage}
            {...(slideChange === undefined ? {} : { happened: slideChange.label })}
            {...(slideSize === undefined ? {} : { size: slideSize })}
            onOpen={() => onOpenCanvas(artifact!.canvas)}
          />
        ) : (
          <div className="text-[0.95rem] leading-7">
            <MarkdownRenderer>{message.content}</MarkdownRenderer>
          </div>
        )}
        {!isUser && citationIDs.length > 0 && (
          <div className="mt-1.5 flex min-w-0" aria-label={`Sources for message ${message.messageId}`}>
            <SourceSummaryAction
              count={citationIDs.length}
              messageID={message.messageId}
              sourceDetails={sourceDetails}
              onOpenInspector={onOpenInspector}
              loadNames={loadSourceSummaries !== undefined}
            />
          </div>
        )}
        {!isUser && staleness?.state === 'stale' && (
          <p className="mt-2 text-xs font-medium text-warning" title={staleness.reasons.join(', ')}>
            May be out of date
          </p>
        )}
        {!isUser && showArtifact && slideVersion === undefined && (
          <ArtifactCard message={message} onOpenCanvas={onOpenCanvas} />
        )}
      </div>
    </article>
  )
}
